import { FC } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Variante A):
 * Initialen-Kreis mit deterministischer Farbe aus dem Namen.
 * Gleicher Name → gleiche Farbe. 6er-Palette aus theme.palette.
 *
 * Bei leerem Namen: neutraler `?`-Kreis in grey.100.
 */
export interface AvatarProps {
  name: string;
  size?: number;
  /**
   * Überschreibt die automatische Initialen-Ableitung aus dem Namen
   * (z.B. feste Pool-Kürzel, Feinschliff 1). Farbe bleibt weiterhin
   * deterministisch aus `name`.
   */
  initials?: string;
}

const PALETTE_KEYS = [
  'primary.main',       // #036A8B
  'primary.altLight',   // #0A669E
  'secondary.main',     // #CFA460
  'secondary.dark',     // #B08840
  'secondary.darkGrey', // #3D4655
  'primary.darker',     // #212A48
] as const;

/**
 * Einfacher, stabiler Hash (String → Non-negative Int).
 * Nicht kryptografisch — Zweck ist deterministische Farb-Auswahl.
 */
const hashName = (name: string): number => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }
  return Math.abs(hash);
};

/**
 * Liefert bis zu 2 Initialen aus dem Namen (uppercase). Splittet an
 * Whitespace, nimmt erstes Zeichen des ersten Tokens plus erstes
 * Zeichen des letzten Tokens (falls > 1 Token).
 */
export const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const tokens = trimmed.split(/\s+/);
  if (tokens.length === 1) {
    return tokens[0].slice(0, 2).toUpperCase();
  }
  const first = tokens[0][0] ?? '';
  const last = tokens[tokens.length - 1][0] ?? '';
  return (first + last).toUpperCase();
};

/**
 * Wählt deterministisch aus der 6er-Palette.
 */
export const pickColor = (name: string): (typeof PALETTE_KEYS)[number] => {
  const trimmed = name.trim();
  if (!trimmed) return PALETTE_KEYS[0];
  return PALETTE_KEYS[hashName(trimmed) % PALETTE_KEYS.length];
};

const Avatar: FC<AvatarProps> = ({ name, size = 24, initials: initialsProp }) => {
  const initials = initialsProp ?? getInitials(name);
  const hasName = initials.length > 0;
  const bgColor = hasName ? pickColor(name) : 'grey.100';
  // Navy bleibt im Dark Mode dunkel (Decision #248) — dort weisse Schrift; alle
  // anderen Toene werden im Dunkeln hell und tragen `contrastText` (dann dunkel).
  // Im Hellen ist beides Weiss.
  const textColor = !hasName ? 'grey.800' : bgColor === 'primary.darker' ? 'common.white' : 'primary.contrastText';

  return (
    <Box
      component="span"
      aria-label={hasName ? `Initialen ${initials}` : 'Kein Name'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        bgcolor: bgColor,
        flexShrink: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: Math.max(10, Math.round(size * 0.46)),
          fontWeight: 600,
          lineHeight: 1,
          color: textColor,
          letterSpacing: 0,
        }}
      >
        {hasName ? initials : '?'}
      </Typography>
    </Box>
  );
};

export default Avatar;
